import { UserPermissions, UserSubscription } from '@creditsights/api-ui-web';
import { orderBy } from 'lodash';
import { DealMarketStatus } from '../../models/CompanyDeal';
import { Security, SecurityDocument, SecurityRating } from '../../models/Securities';

const TABS_CONFIG: Record<UserSubscription, DealMarketStatus[]> = {
  [UserSubscription.CS_CR_LFI]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Expected, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.CS_CR]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.CS_LFI]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Expected, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.CR_LFI]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Expected, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.CS]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.CR]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.LFI]: [DealMarketStatus.All, DealMarketStatus.InMarket, DealMarketStatus.Expected, DealMarketStatus.Completed, DealMarketStatus.Hung],
  [UserSubscription.RP]: [],
};

const OFFER_MEMORANDUM_TYPES = ['Offer Memorandum']; // 'Prospectus: Reds', 'Prospectus: Final'

// used also by @creditsights/creditsights-web MFE
export const getDealsTabs = ({ userSubscription }: UserPermissions): DealMarketStatus[] => {
  return TABS_CONFIG[userSubscription];
};

export const getDocumentUrl = (doc: SecurityDocument): string => {
  return `/api/common/v1/securities/deals/files/${doc.bond_id}/${encodeURIComponent(doc.doc_name)}`;
};

export const getOfferMemorandumDocument = (security: Security): SecurityDocument | null => {
  const documents = security?.docs?.filter((x) => OFFER_MEMORANDUM_TYPES.includes(x.doc_type));
  if (!documents?.length) {
    return null;
  }

  return orderBy(documents, (x) => x.doc_date, 'desc')[0];
};

export const mapRatings = (ratingsData: SecurityRating[]): { title: string; rating: string }[] => {
  const agencyOrder = ["Moody's", 'S&P', 'Fitch'];
  const agencyShort = { "Moody's": 'M', 'S&P': 'S', Fitch: 'F' };

  const formattedRatings = {
    'Corp Rating': [],
    'Facility Rating': [],
    'Corp Rating (Expected)': [],
    'Facility Rating (Expected)': [],
  };

  const ratedByAgencies = {
    'Corp Rating': [],
    'Facility Rating': [],
    'Corp Rating (Expected)': [],
    'Facility Rating (Expected)': [],
  };

  const displayRules = {
    'Actual Rating, Corp Rating': 'Corp Rating',
    'Actual Rating, Facility Rating': 'Facility Rating',
    'Expected Rating, Corp Rating': 'Corp Rating (Expected)',
    'Expected Rating, Facility Rating': 'Facility Rating (Expected)',
  };

  Object.entries(displayRules).forEach(([key, label]) => {
    const [status, ratingType] = key.split(', ');
    const filteredRatings = ratingsData.filter((r) => r.status === status && r.type === ratingType);

    const ratingsByAgency = {};
    agencyOrder.forEach((agency) => {
      ratingsByAgency[agency] = '';
    });

    filteredRatings.forEach((rating) => {
      if (agencyOrder.includes(rating.agency)) {
        ratingsByAgency[rating.agency] = rating.rating;
      }
    });

    const formattedRow = agencyOrder
      .map((agency) => {
        if (ratingsByAgency[agency]) {
          ratedByAgencies[label].push(agency);
          return `${ratingsByAgency[agency]}`;
        }
        return '';
      })
      .filter((fr) => fr.length > 0);

    if (formattedRow.length > 0) {
      formattedRatings[label].push(formattedRow.join('/'));
    }
  });

  const result: { title: string; rating: string }[] = [];
  Object.entries(formattedRatings).forEach(([key, values]) => {
    if (values.length > 0) {
      const title = `${key} (${ratedByAgencies[key].map((agency) => agencyShort[agency]).join('/')}) `;
      const ratings = `${values.join(' ')}`;
      result.push({ title, rating: ratings });
    }
  });

  return result;
};
